import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy
} from "firebase/firestore";
import { db } from "../../../firebase/firestore.js";

const CUSTOMERS_COLLECTION = "customers";
const SHIPPING_SUBCOLLECTION = "shippingAddresses";

/**
 * Fetch all customers ordered by name
 */
export async function getCustomers() {
  const colRef = collection(db, CUSTOMERS_COLLECTION);
  try {
    const q = query(colRef, orderBy("name", "asc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate().toISOString() : d.data().createdAt
    }));
  } catch (_) {
    const snapshot = await getDocs(colRef);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate().toISOString() : d.data().createdAt
    }));
  }
}

/**
 * Get single customer by ID
 */
export async function getCustomerById(id) {
  const docRef = doc(db, CUSTOMERS_COLLECTION, id);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Create a new customer document
 */
export async function createCustomer(customerData) {
  const colRef = collection(db, CUSTOMERS_COLLECTION);
  const docRef = await addDoc(colRef, {
    partyType: customerData.partyType || "customer",
    name: customerData.name.trim(),
    mobile: customerData.mobile || "",
    email: customerData.email || "",
    gstin: (customerData.gstin || "").toUpperCase().trim(),
    stateCode: customerData.stateCode || "33",
    state: customerData.state || "Tamil Nadu",
    city: customerData.city || "",
    billingAddress: customerData.billingAddress || {},
    bankDetails: customerData.bankDetails || {},
    openingBalance: Number(customerData.openingBalance || 0),
    companyIds: Array.isArray(customerData.companyIds) ? customerData.companyIds : [],
    openingBalances: customerData.openingBalances || {},
    customPrices: customerData.customPrices || {},
    status: customerData.status || "active",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
}

/**
 * Update an existing customer
 */
export async function updateCustomer(id, customerData) {
  const docRef = doc(db, CUSTOMERS_COLLECTION, id);
  const payload = {
    updatedAt: serverTimestamp()
  };
  if (customerData.name !== undefined) payload.name = customerData.name.trim();
  if (customerData.mobile !== undefined) payload.mobile = customerData.mobile || "";
  if (customerData.email !== undefined) payload.email = customerData.email || "";
  if (customerData.gstin !== undefined) payload.gstin = (customerData.gstin || "").toUpperCase().trim();
  if (customerData.stateCode !== undefined) payload.stateCode = customerData.stateCode || "33";
  if (customerData.state !== undefined) payload.state = customerData.state || "Tamil Nadu";
  if (customerData.city !== undefined) payload.city = customerData.city || "";
  if (customerData.billingAddress !== undefined) payload.billingAddress = customerData.billingAddress || {};
  if (customerData.bankDetails !== undefined) payload.bankDetails = customerData.bankDetails || {};
  if (customerData.openingBalance !== undefined) payload.openingBalance = Number(customerData.openingBalance || 0);
  if (customerData.partyType !== undefined) payload.partyType = customerData.partyType;
  if (customerData.status !== undefined) payload.status = customerData.status || "active";
  if (customerData.customPrices !== undefined) payload.customPrices = customerData.customPrices;
  if (customerData.companyIds !== undefined) payload.companyIds = Array.isArray(customerData.companyIds) ? customerData.companyIds : [];
  if (customerData.openingBalances !== undefined) payload.openingBalances = customerData.openingBalances || {};

  await updateDoc(docRef, payload);
}

/**
 * Update or save custom item prices for a specific customer
 */
export async function updateCustomerCustomPrices(customerId, priceMap = {}) {
  if (!customerId) return;
  const docRef = doc(db, CUSTOMERS_COLLECTION, customerId);
  await updateDoc(docRef, {
    customPrices: priceMap,
    updatedAt: serverTimestamp()
  });
}

/**
 * Delete a customer
 */
export async function deleteCustomer(id) {
  const docRef = doc(db, CUSTOMERS_COLLECTION, id);
  await deleteDoc(docRef);
}

/**
 * Fetch shipping addresses for a specific customer
 */
export async function getCustomerShippingAddresses(customerId) {
  const addressesRef = collection(db, CUSTOMERS_COLLECTION, customerId, SHIPPING_SUBCOLLECTION);
  const snapshot = await getDocs(addressesRef);
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data()
  }));
}

/**
 * Add a shipping address to a customer subcollection
 */
export async function addCustomerShippingAddress(customerId, addressData) {
  const addressesRef = collection(db, CUSTOMERS_COLLECTION, customerId, SHIPPING_SUBCOLLECTION);
  const docRef = await addDoc(addressesRef, {
    ...addressData,
    createdAt: serverTimestamp()
  });
  return docRef.id;
}

/**
 * Delete a shipping address from a customer subcollection
 */
export async function deleteCustomerShippingAddress(customerId, addressId) {
  const addressRef = doc(db, CUSTOMERS_COLLECTION, customerId, SHIPPING_SUBCOLLECTION, addressId);
  await deleteDoc(addressRef);
}
